import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';
import styled from 'styled-components';

import { formatPrice } from '../../../../shared/helpers';
import { Subtext } from '../../styled/components/order-item-subtext';
import OrderItemSubtext from '../order-summary/order-item-subtext';

import type { Currency } from '../../../../../types/currency';

// Spinners supressed to not confuse
const QuantityInput = styled.input`
  width: 3.5rem;
  text-align: center;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  appearance: textfield;
`;

const StepperGlyph = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

type Props = {
  name: string | null | undefined;
  description?: string;
  quantity: number;
  lineTotal: number;
  currency?: Currency;
  minQuantity?: number;
  maxQuantity?: number;
  strikethrough?: boolean;
  // Per-return rate once the account exceeds the return count it's purchasing for this year (BRGUI-2975)
  // -- only Managed Returns publishes overagePricingTiers today, so this stays undefined (and the
  // warning hidden) for every other adjustable-quantity offer reusing this row.
  overagePrice?: number | null;
  onChangeQuantity: (_: number) => void;
};

const OrderReviewQuantityRow = ({
  name,
  description,
  quantity,
  lineTotal,
  currency,
  minQuantity = 1,
  maxQuantity,
  strikethrough = false,
  overagePrice,
  onChangeQuantity,
}: Props) => {
  const canDecrease = quantity > minQuantity;
  const canIncrease = maxQuantity == null || quantity < maxQuantity;

  // Clamp rather than trust the field: a user can type any number (or clear it entirely, giving NaN)
  // regardless of the min/max attributes, and the resulting quantity feeds a price.
  const handleTypedChange = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return;

    const upperBound = maxQuantity ?? parsed;
    onChangeQuantity(Math.min(Math.max(parsed, minQuantity), upperBound));
  };

  return (
    <SRow className='pad-top-xs align-items-center'>
      <SCol span='8'>
        <span>{name}</span>
        {description && <OrderItemSubtext text={description} />}
        {overagePrice != null && (
          <Subtext className='text-xs' data-ref='buy.order-review.quantity.overage-warning'>
            {t('client.order-review.quantity.overage-warning', {
              overagePrice: formatPrice(overagePrice, 2, currency),
            })}
          </Subtext>
        )}
      </SCol>

      <SCol span='2' className='flex align-items-center justify-content-center'>
        <button
          type='button'
          className='ghost-blue icon'
          data-ref='buy.order-review.quantity.decrease'
          aria-label={t('client.accessibility.offer-quantity-decrease')}
          disabled={!canDecrease}
          onClick={() => onChangeQuantity(quantity - 1)}
        >
          <StepperGlyph aria-hidden='true'>&minus;</StepperGlyph>
        </button>

        <QuantityInput
          type='number'
          className='inline margin-left-xs margin-right-xs'
          data-ref='buy.order-review.quantity.input'
          aria-label={t('client.accessibility.offer-quantity')}
          value={quantity}
          min={minQuantity}
          max={maxQuantity}
          onChange={(e) => handleTypedChange(e.target.value)}
        />

        <button
          type='button'
          className='ghost-blue icon'
          data-ref='buy.order-review.quantity.increase'
          aria-label={t('client.accessibility.offer-quantity-increase')}
          disabled={!canIncrease}
          onClick={() => onChangeQuantity(quantity + 1)}
        >
          <StepperGlyph aria-hidden='true'>+</StepperGlyph>
        </button>
      </SCol>

      <SCol
        span='2'
        data-ref='buy.order-review.quantity.line-total'
        className={`text-right${strikethrough ? ' text-strikethrough' : ''}`}
      >
        <span>{formatPrice(lineTotal, 2, currency)}</span>
      </SCol>
    </SRow>
  );
};

export default OrderReviewQuantityRow;
